import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { ColumnDef, DataTable } from '../../Components/Table/DataTable';
import { Header } from '../../Components/Header/Header';
import { FreshnessBadge } from '../../Components/Vending/FreshnessBadge';
import { decode } from '../../Services/DecodeToken';
import vendingApiClient from '../../Services/VendingApi';
import { formatUTC } from '../../Services/FormatUtc';
import { PageContainer } from '../../Styles/AppStyles';
import { COLORS } from '../../Styles/Colors';
import {
  VendingMappedCompany,
  VendingMeta,
} from '../../Types/vending';

const SEARCH_DEBOUNCE_MS = 400;

const formatTxnDate = (value: string | null | undefined) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

interface TxnRow {
  id: number;
  machine_id: number;
  machine_name: string | null;
  product_name: string | null;
  employee_name: string | null;
  location_key: string | null;
  quantity: number;
  unit_price: number | null;
  vend_timestamp: string;
  transaction_status: string | null;
  cid: number | null;
}

interface MachineOption {
  id: number;
  machine_name: string;
  provider_machine_id: number;
  cid?: number | null;
}

interface TxnListResponse {
  data: TxnRow[];
  meta: VendingMeta & {
    total?: number;
    companies?: VendingMappedCompany[];
    machines?: MachineOption[];
  };
}

const statusChip = (status: string | null) => {
  const value = (status || 'unknown').toLowerCase();
  let color: string = COLORS.lightGray;
  let bg = `${COLORS.lightGray}18`;
  if (value.includes('success') || value === 'ok' || value === 'complete' || value === 'completed') {
    color = COLORS.success;
    bg = `${COLORS.success}18`;
  } else if (value.includes('fail') || value.includes('error') || value.includes('cancel')) {
    color = COLORS.red;
    bg = `${COLORS.red}12`;
  } else if (value.includes('pend') || value.includes('process')) {
    color = COLORS.orange;
    bg = `${COLORS.orange}18`;
  }
  return (
    <Chip
      size="small"
      label={status || '—'}
      sx={{
        height: 22,
        fontSize: '0.7rem',
        fontWeight: 600,
        textTransform: 'capitalize',
        bgcolor: bg,
        color,
      }}
    />
  );
};

export const VendingTransactionsPage = () => {
  const navigate = useNavigate();
  const token: any = decode();
  const isAbatixAdmin = Number(token?.roles?.level) >= 100;

  const [rows, setRows] = useState<TxnRow[]>([]);
  const [companies, setCompanies] = useState<VendingMappedCompany[]>([]);
  const [machines, setMachines] = useState<MachineOption[]>([]);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [machineFilter, setMachineFilter] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setMachineFilter('');
  }, [companyFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (statusFilter) params.set('status', statusFilter);
      if (isAbatixAdmin && companyFilter) params.set('cid', companyFilter);
      if (machineFilter) params.set('machine_id', machineFilter);
      const response = await vendingApiClient(`/transactions?${params.toString()}`);
      const json: TxnListResponse = await response.json();
      setRows(json.data);
      setCompanies(json.meta?.companies ?? []);
      setMachines(json.meta?.machines ?? []);
      setAsOf(json.meta?.as_of ?? null);
      setTotal(json.meta?.total ?? json.data.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load transactions');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter, companyFilter, machineFilter, isAbatixAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const hasActiveFilters = Boolean(
    debouncedSearch
    || statusFilter
    || (isAbatixAdmin && companyFilter)
    || machineFilter,
  );

  const clearFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setStatusFilter('');
    setCompanyFilter('');
    setMachineFilter('');
  };

  const columns: ColumnDef<TxnRow>[] = useMemo(() => [
    {
      field: 'vend_timestamp',
      headerName: 'Date',
      width: 180,
      minWidth: 160,
      valueGetter: (_v, row) => formatTxnDate(row.vend_timestamp),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: COLORS.darkGray, whiteSpace: 'nowrap' }}>
          {formatTxnDate(params.row.vend_timestamp)}
        </Typography>
      ),
    },
    {
      field: 'machine_name',
      headerName: 'Machine',
      width: 200,
      minWidth: 160,
      searchable: true,
      valueGetter: (_v, row) => row.machine_name || `Machine ${row.machine_id}`,
      renderCell: (params) => (
        <Typography
          variant="body2"
          noWrap
          title={params.row.machine_name || `Machine ${params.row.machine_id}`}
          sx={{ color: COLORS.teal, fontWeight: 600, cursor: 'pointer', maxWidth: '100%' }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/vending/machines/${params.row.machine_id}`);
          }}
        >
          {params.row.machine_name || `Machine ${params.row.machine_id}`}
        </Typography>
      ),
    },
    {
      field: 'product_name',
      headerName: 'Product',
      width: 260,
      minWidth: 160,
      searchable: true,
      valueGetter: (_v, row) => row.product_name || '—',
      renderCell: (params) => (
        <Typography
          variant="body2"
          noWrap
          title={params.row.product_name || '—'}
          sx={{ maxWidth: '100%' }}
        >
          {params.row.product_name || '—'}
        </Typography>
      ),
    },
    {
      field: 'employee_name',
      headerName: 'User',
      width: 160,
      minWidth: 120,
      searchable: true,
      valueGetter: (_v, row) => row.employee_name || '—',
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.row.employee_name || '—'}>
          {params.row.employee_name || '—'}
        </Typography>
      ),
    },
    {
      field: 'location_key',
      headerName: 'Slot',
      width: 90,
      minWidth: 70,
      valueGetter: (_v, row) => row.location_key || '—',
    },
    {
      field: 'quantity',
      headerName: 'Qty',
      width: 80,
      minWidth: 70,
      type: 'number',
    },
    {
      field: 'unit_price',
      headerName: 'Unit price',
      width: 110,
      minWidth: 90,
      valueGetter: (_v, row) => (
        row.unit_price != null ? `$${Number(row.unit_price).toFixed(2)}` : '—'
      ),
    },
    {
      field: 'transaction_status',
      headerName: 'Status',
      width: 130,
      minWidth: 110,
      renderCell: (params) => statusChip(params.row.transaction_status),
    },
  ], [navigate]);

  const tableRows = useMemo(
    () => rows.map((r) => ({
      ...r,
      machine_name: r.machine_name || `Machine ${r.machine_id}`,
      product_name: r.product_name || '—',
    })),
    [rows],
  );

  const subtitle = useMemo(() => {
    const parts = [`${total} total`];
    if (asOf) parts.push(`As of ${formatUTC(asOf)} UTC`);
    return parts.join(' · ');
  }, [total, asOf]);

  return (
    <PageContainer>
      <Header title="Transactions" subtitle={subtitle} count={rows.length}>
        <FreshnessBadge asOf={asOf} />
        <Button variant="outlined" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Header>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: { xs: 2, md: 3 },
            pb: 2,
            flexShrink: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            size="small"
            label="Search product"
            placeholder="Product name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 220, flex: { xs: '1 1 100%', md: '0 1 240px' } }}
          />

          {isAbatixAdmin && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Customer</InputLabel>
              <Select
                label="Customer"
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
              >
                <MenuItem value="">All customers</MenuItem>
                {companies.map((c) => (
                  <MenuItem key={c.cid} value={String(c.cid)}>
                    {c.customer_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Machine</InputLabel>
            <Select
              label="Machine"
              value={machineFilter}
              onChange={(e) => setMachineFilter(e.target.value)}
            >
              <MenuItem value="">All machines</MenuItem>
              {machines.map((m) => (
                <MenuItem key={m.id} value={String(m.id)}>
                  {m.machine_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Success">Success</MenuItem>
              <MenuItem value="Failed">Failed</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
            </Select>
          </FormControl>

          {hasActiveFilters && (
            <Button variant="text" onClick={clearFilters}>Clear Filters</Button>
          )}
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6, flexShrink: 0 }}>
            <CircularProgress sx={{ color: COLORS.teal }} />
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ px: { xs: 2, md: 3 }, flexShrink: 0 }}>
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
            >
              {error}
            </Alert>
          </Box>
        )}

        {!loading && !error && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', width: '100%' }}>
            <DataTable
              columns={columns}
              data={tableRows}
              pageKey="vending-transactions"
              idField="id"
              loading={loading}
              rowHeight={52}
              onRowClick={(row) => navigate(`/vending/machines/${row.machine_id}`)}
            />
          </Box>
        )}
      </Box>
    </PageContainer>
  );
};
